import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { quoteService, clientService, productService } from '../../services/api';
import { Client, Product } from '../../types';
import { toast } from 'react-toastify';
import { TrashIcon } from '../common/Icons';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

interface QuoteFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  quote?: any;
}

interface QuoteItem {
  product_id: number | null;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  is_free_service: boolean;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ show, onHide, onSuccess, quote }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [formData, setFormData] = useState({
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    terms: '',
  });

  const [items, setItems] = useState<QuoteItem[]>([]);

  // ---------- FORMAT DATE ----------
  const formatDate = (date: any) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  // ---------- LOAD DATA ----------
  useEffect(() => {
    if (show) {
      fetchClients();
      fetchProducts();
    }
  }, [show]);

  // ---------- EDIT MODE ----------
  useEffect(() => {
    if (quote && show) {
      setFormData({
        client_id: quote.client_id?.toString() || '',
        date: formatDate(quote.date),
        valid_until: formatDate(quote.valid_until),
        notes: quote.notes || '',
        terms: quote.terms || '',
      });

      setItems(
        quote.items?.map((item: any) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          is_free_service: item.product_id === null,
        })) || []
      );
    }
  }, [quote, show]);

  // ---------- API ----------
  const fetchClients = async () => {
    try {
      const res = await clientService.getAll();
      setClients(res.data.data);
    } catch {}
  };

  const fetchProducts = async () => {
    try {
      const res = await productService.getAll();
      setProducts(res.data.data);
    } catch {}
  };

  // ---------- FORM ----------
  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ---------- ITEMS ----------
  const addItem = () => {
    setItems(prev => [
      ...prev,
      {
        product_id: null,
        product_name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        is_free_service: false,
      },
    ]);
  };

  const addFreeService = () => {
    setItems(prev => [
      ...prev,
      {
        product_id: null,
        product_name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        is_free_service: true,
      },
    ]);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // ---------- UPDATE ITEM FIX (IMPORTANT) ----------
  const updateItem = (index: number, field: keyof QuoteItem, value: any) => {
    setItems(prev =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updated = { ...item, [field]: value };

        if (field === 'product_id' && value && !item.is_free_service) {
          const product = products.find(p => p.id === Number(value));

          if (product) {
            updated.product_name = product.name;
            updated.description = product.description || '';
            updated.unit_price = product.selling_price_ht;
          }
        }

        return updated;
      })
    );
  };

  // ---------- SUBMIT ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }

    setLoading(true);

    try {
      const data = {
        ...formData,
        client_id: Number(formData.client_id),
        items: items.map(item => ({
          product_id: item.is_free_service ? null : item.product_id,
          product_name: item.product_name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      };

      if (quote) {
        await quoteService.update(quote.id, data);
        toast.success('Devis modifié avec succès');
      } else {
        await quoteService.create(data);
        toast.success('Devis créé avec succès');
      }

      onSuccess();
      onHide();
      resetForm();
    } catch {
      toast.error('Erreur lors de l’enregistrement du devis');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      date: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      notes: '',
      terms: '',
    });
    setItems([]);
  };

  const getTotalAmount = () =>
    items.reduce((t, i) => t + i.quantity * i.unit_price, 0);

  // ---------- UI ----------
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{quote ? 'Modifier devis' : 'Nouveau devis'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>

          {/* CLIENT + DATES */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Select
                name="client_id"
                value={formData.client_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Client</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Control type="date" name="date" value={formData.date} onChange={handleInputChange} />
            </Col>

            <Col md={3}>
              <Form.Control type="date" name="valid_until" value={formData.valid_until} onChange={handleInputChange} />
            </Col>
          </Row>

          {/* ITEMS */}
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-2">
              <h6>Produits / Services</h6>
              <div>
                <Button size="sm" onClick={addItem}>Produit</Button>
                <Button size="sm" className="ms-2" onClick={addFreeService}>Service</Button>
              </div>
            </div>

            <Table bordered>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Description</th>
                  <th>Qté</th>
                  <th>Prix</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>

                    {/* PRODUCT SELECT FIXED */}
                    <td>
                      {item.is_free_service ? (
                        <Form.Control
                          value={item.product_name}
                          onChange={(e) =>
                            updateItem(index, 'product_name', e.target.value)
                          }
                          placeholder="Service"
                        />
                      ) : (
                        <Form.Select
                          value={item.product_id ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateItem(index, 'product_id', val ? Number(val) : null);
                          }}
                        >
                          <option value="">Produit</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </td>

                    <td style={{ minWidth: 250 }}>
                      <CKEditor
                        editor={ClassicEditor as any}
                        data={item.description}
                        onChange={(e, editor) =>
                          updateItem(index, 'description', editor.getData())
                        }
                      />
                    </td>

                    <td>
                      <Form.Control
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(index, 'quantity', Number(e.target.value))
                        }
                      />
                    </td>

                    <td>
                      <Form.Control
                        type="number"
                        value={item.unit_price}
                        onChange={(e) =>
                          updateItem(index, 'unit_price', Number(e.target.value))
                        }
                      />
                    </td>

                    <td>{(item.quantity * item.unit_price).toFixed(2)}</td>

                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeItem(index)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>

                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* TOTAL */}
          <div className="text-end">
            <h5>Total: {getTotalAmount().toFixed(2)} MAD</h5>
          </div>

        </Modal.Body>

        <Modal.Footer>
          <Button onClick={onHide}>Annuler</Button>
          <Button type="submit" disabled={loading}>
            {quote ? 'Modifier' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default QuoteForm;